import React, { useContext, useEffect, useState } from "react";
import NavbarTop from "../components/navbar/NavbarTop";
import NavbarVertical from "../components/navbar/NavbarVertical";
import Footer from "../components/footer/Footer";
import AppContext, {
  CurrencyContext,
  NotificationContext,
  SocketContext,
} from "../context/Context";
import DashboardRoutes from "../routes/DashboardRoutes";
import { useSelector } from "react-redux";
import moment from "moment";
import { checkUserToken } from "../helpers/api/auth";
import handleErrorMessage from "../helpers/errors/handleErrorMessage";
import { fetchDayRate, findAllExchangeRates } from "../helpers/api/currency";
import { rateEffectiveDateKey } from "../helpers/rateEffectiveDate";
import { findOneConfig } from "../helpers/api/condominium";
import { Toast } from "../components/UI/toast/Toast";
import { isIterableArray } from "../helpers/utils";
import { getCurrencyName, getCurrencySymbol } from "../helpers/currencyConversion";
import RewardsCelebrations from "../components/rewards/RewardsCelebrations";
import { REWARDS_ENABLED } from "../helpers/rewardsEnabled";
import { fetchAllPages } from "../helpers/api/pagination";

// Elige la tasa "vigente hoy" de una lista de ExchangeRate: la de mayor
// `date` que no sea futura (con `created_at` como desempate/respaldo para
// filas antiguas sin `date`). Antes se elegía simplemente la fila más
// recientemente CREADA, sin mirar `date` — eso hacía que registrar una tasa
// para una fecha pasada (ej. desde "Registrar tasa" en un gasto atrasado)
// apareciera en este banner como si fuera la tasa de hoy, porque su
// created_at sí era hoy aunque su date no lo fuera.
const pickActiveExchangeRate = (rates) => {
  const todayKey = moment().format("YYYY-MM-DD");
  const effectiveKey = rateEffectiveDateKey(todayKey) || todayKey;
  const withEffectiveDate = rates.map((rate) => ({
    ...rate,
    effectiveDateKey: rate.date
      ? moment(rate.date).format("YYYY-MM-DD")
      : moment(rate.created_at).format("YYYY-MM-DD"),
  }));

  const forEffectiveDay = withEffectiveDate.filter(
    (rate) => rate.effectiveDateKey === effectiveKey && Number(rate.value) > 0
  );
  const notFuture = withEffectiveDate.filter((rate) => rate.effectiveDateKey <= todayKey);
  const pool = forEffectiveDay.length
    ? forEffectiveDay
    : notFuture.length
    ? notFuture
    : withEffectiveDate;

  return pool.sort((a, b) => {
    if (a.effectiveDateKey !== b.effectiveDateKey) {
      return b.effectiveDateKey.localeCompare(a.effectiveDateKey);
    }
    return moment(b.created_at).valueOf() - moment(a.created_at).valueOf();
  })[0];
};

const normalizeExchangeRate = (rate) => {
  if (!rate) {
    return null;
  }

  return {
    ...rate,
    value: Number(rate?.value ?? 0),
  };
};

const DashboardLayout = () => {
  const { isFluid, isTopNav } = useContext(AppContext);
  const socket = useContext(SocketContext);
  const { setNotifications } = useContext(NotificationContext);
  const { setCurrency, setLoading, setBaseCurrency } = useContext(CurrencyContext);
  const { currency } = useSelector((state) => state.auth);
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const { user } = useSelector((state) => state.auth);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    if (socket) {
      user?.role === "property" &&
        socket
          .private(`notification-voting.${selectedCondominium}`)
          .listen("VotingEvent", ({ response }) => {
            setNotifications((prev) => [
              {
                created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
                data: JSON.stringify(response),
                type: "voting",
                read_at: null,
              },
              ...prev,
            ]);
          });
    }
    return () => {
      socket && socket.leave(`notification-voting.${selectedCondominium}`);
    };
  }, [socket]);

  useEffect(() => {
    let isMounted = true;
    if (user?.token) {
      checkUserToken(user?.token)
        .then((res) => {
          if (isMounted) {
            setUserId(res.id);
          }
        })
        .catch((err) => {
          if (isMounted) {
            handleErrorMessage(err);
          }
        });
    }
    return () => {
      isMounted = false;
    };
  }, [user?.token]);

  useEffect(() => {
    if (userId && socket) {
      (user?.role === "property" || user?.role === "manager") &&
        socket
          .private(`notification-complaint.${userId}`)
          .listen("ComplaintEvent", ({ response }) => {
            setNotifications((prev) => [
              {
                created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
                data: JSON.stringify(response),
                type: "new-complaint",
                read_at: null,
              },
              ...prev,
            ]);
          });

      if (user?.role === "manager") {
        // TICKET COMO MANAGER
        socket
          .private(`notification-ticket.${userId}`)
          .listen("TicketEvent", (res) => {
            setNotifications((prev) => [
              {
                created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
                data: JSON.stringify(res),
                type: "response-ticket",
                read_at: null,
              },
              ...prev,
            ]);
          });

        /* MANAGER */
        socket
          .private(`notification-property-payment.${userId}`)
          .listen("PropertyPaymentEvent", (res) => {
            setNotifications((prev) => [
              {
                created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
                data: JSON.stringify(res),
                type: "property-payment",
                read_at: null,
              },
              ...prev,
            ]);
          });

        /* MANAGER */
        socket
          .private(`notification-reserved-common-area.${userId}`)
          .listen("ReservedCommonAreaEvent", ({ response }) => {
            setNotifications((prev) => [
              {
                created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
                data: JSON.stringify(response),
                type: "reserved-common-area",
                read_at: null,
              },
              ...prev,
            ]);
          });
      }

      // NUEVA CUENTA POR PAGAR PROPIETARIO
      user?.role === "property" &&
        socket
          .private(`notification-debts-to-pay.${userId}`)
          .listen("DebtsToPayEvent", (res) => {
            setNotifications((prev) => [
              {
                created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
                data: JSON.stringify(res),
                type: "new-debts-to-pay",
                read_at: null,
              },
              ...prev,
            ]);
          });
    }
    return () => {
      socket && socket.leave(`notification-complaint.${userId}`);
      socket && socket.leave(`notification-ticket.${userId}`);
      socket && socket.leave(`notification-property-payment.${userId}`);
      socket && socket.leave(`notification-reserved-common-area.${userId}`);
      socket && socket.leave(`notification-debts-to-pay.${userId}`);
    };
  }, [userId, socket]);

  useEffect(() => {
    let isMounted = true;
    if (currency) {
      setLoading(true);
      fetchDayRate({ condominium_id: selectedCondominium })
        .then((rate) => {
          if (!rate || !(Number(rate.value) > 0)) {
            throw new Error("Sin tasa del día");
          }
          if (isMounted) {
            setCurrency(normalizeExchangeRate(rate));
          }
        })
        .catch(() =>
          fetchAllPages((page, pageSize) => findAllExchangeRates(page, pageSize, { currency }))
            .then(({ data }) => {
              if (isMounted) {
                isIterableArray(data)
                  ? setCurrency(normalizeExchangeRate(pickActiveExchangeRate(data)))
                  : setCurrency(null);
              }
            })
        )
        .catch(() => {
          if (isMounted) {
            Toast.fire({
              type: "error",
              title: "Ha ocurrido un error al obtener la tasa de cambio.",
            });
          }
        })
        .finally(() => {
          if (isMounted) {
            setLoading(false);
          }
        });
    } else {
      setCurrency(null);
    }
    return () => {
      isMounted = false;
    };
  }, [currency, selectedCondominium]);

  useEffect(() => {
    let isMounted = true;
    if (selectedCondominium) {
      findOneConfig(selectedCondominium)
        .then(({ data }) => {
          if (isMounted) {
            const iso = data?.base_currency || "USD";
            setBaseCurrency({
              iso,
              symbol: getCurrencySymbol(iso),
              name: getCurrencyName(iso),
            });
          }
        })
        .catch(() => {
          if (isMounted) {
            setBaseCurrency({ iso: "USD", symbol: "$", name: "Dolar" });
          }
        });
    }
    return () => {
      isMounted = false;
    };
  }, [selectedCondominium]);

  return (
    <div className={isFluid ? "container-fluid" : "container"}>
      {!isTopNav && <NavbarVertical />}
      <div className="content">
        <NavbarTop />
        <DashboardRoutes />
        <Footer />
      </div>
      {/* HO-TASK-290: celebraciones de Homelan Rewards (solo propietarios). */}
      {REWARDS_ENABLED && user?.role === "property" && <RewardsCelebrations />}
    </div>
  );
};

export default DashboardLayout;
